import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { formatRelativeTime } from "../utils/dateUtils";

import type {
  AttentionEscalation,
  AttentionLowSentiment,
  AttentionNegativeFeedback,
  AttentionSilentGenie,
  NeedsAttention as NeedsAttentionData,
} from "@/types/activity";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
interface NeedsAttentionProps {
  data: NeedsAttentionData | undefined;
  isLoading: boolean;
  isError?: boolean;
}

const sectionConfig = {
  escalations: {
    genieIcon: "eye",
    title: "Escalations",
    blurb: "Calls your genies handed off — review what happened and follow up.",
  },
  lowSentiment: {
    genieIcon: "smile",
    title: "Low sentiment",
    blurb: "Conversations that left callers frustrated. A quick listen can save the relationship.",
  },
  negativeFeedback: {
    genieIcon: "chat-check",
    title: "Negative feedback",
    blurb: "Direct thumbs-down from end users on your widget.",
  },
  silentGenies: {
    genieIcon: "genie-lamp",
    title: "Silent genies",
    blurb: "Live genies that had zero conversations in the last 7 days.",
  },
} as const;

const SectionHeader = ({
  iconUrl,
  title,
  blurb,
  count,
}: {
  iconUrl: string;
  title: string;
  blurb: string;
  count: number;
}) => (
  <div className="flex items-start gap-3 mb-3">
    <div className="h-9 w-9 rounded-xl bg-genie-primary/10 flex items-center justify-center flex-shrink-0">
      <img src={iconUrl} alt="" className="h-5 w-5" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <span className="text-xs font-medium text-genie-secondary bg-genie-secondary/10 px-2 py-0.5 rounded-full">
          {count}
        </span>
      </div>
      <p className="text-xs text-gray-500 mt-0.5">{blurb}</p>
    </div>
  </div>
);

const RowShell = forwardRef<
  HTMLAnchorElement,
  {
    to: string;
    agentName: string;
    agentBrandColor: string | null;
    primary: React.ReactNode;
    secondary?: React.ReactNode;
    occurredAt: string | null;
  }
>(({ to, agentName, agentBrandColor, primary, secondary, occurredAt }, ref) => {
  const dotColor = agentBrandColor || "#4E9CFF";
  return (
    <Link
      ref={ref}
      to={to}
      className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9FAFC] transition-colors group"
    >
      <span className="mt-1.5 h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900 truncate">{agentName}</span>
          {occurredAt ? <span className="text-xs text-gray-500">{formatRelativeTime(occurredAt)}</span> : null}
        </div>
        <p className="text-sm text-gray-700 mt-0.5 line-clamp-2">{primary}</p>
        {secondary ? <div className="text-xs text-gray-500 mt-1">{secondary}</div> : null}
      </div>
      <ChevronRight className="h-4 w-4 text-gray-400 mt-2 flex-shrink-0 group-hover:text-genie-primary transition-colors" />
    </Link>
  );
});
RowShell.displayName = "RowShell";

const EscalationRow = ({ item }: { item: AttentionEscalation }) => (
  <RowShell
    to={item.conversationId ? `/genie-hub/conversations/${item.conversationId}` : "#"}
    agentName={item.agentName}
    agentBrandColor={item.agentBranding?.primaryColor ?? null}
    primary={item.summary || "Escalation triggered — open the conversation for full context."}
    secondary={item.reason ? <span>Reason: {item.reason}</span> : null}
    occurredAt={item.occurredAt}
  />
);

const LowSentimentRow = ({ item }: { item: AttentionLowSentiment }) => (
  <RowShell
    to={item.conversationId ? `/genie-hub/conversations/${item.conversationId}` : "#"}
    agentName={item.agentName}
    agentBrandColor={item.agentBranding?.primaryColor ?? null}
    primary={item.summary || "Conversation ended with negative sentiment."}
    secondary={
      item.score !== null ? (
        <span>
          Sentiment score <span className="font-semibold text-red-600">{item.score.toFixed(2)}</span>
        </span>
      ) : null
    }
    occurredAt={item.occurredAt}
  />
);

const NegativeFeedbackRow = ({ item }: { item: AttentionNegativeFeedback }) => (
  <RowShell
    to={item.conversationId ? `/genie-hub/conversations/${item.conversationId}` : "#"}
    agentName={item.agentName}
    agentBrandColor={item.agentBranding?.primaryColor ?? null}
    primary={item.feedbackText || "User left negative feedback on this conversation."}
    occurredAt={item.occurredAt}
  />
);

const SilentGenieRow = ({ item }: { item: AttentionSilentGenie }) => (
  <RowShell
    to={`/genie-hub/my-genies/${item.agentId}`}
    agentName={item.agentName}
    agentBrandColor={item.agentBranding?.primaryColor ?? null}
    primary="No conversations in the last 7 days."
    secondary={<span>Open the genie to share its link, embed it, or check if it's still needed.</span>}
    occurredAt={null}
  />
);

const SectionBlock = ({
  bucket,
  count,
  children,
}: {
  bucket: keyof typeof sectionConfig;
  count: number;
  children: React.ReactNode;
}) => {
  if (count === 0) return null;
  const config = sectionConfig[bucket];
  return (
    <section>
      <SectionHeader iconUrl={config.genieIcon} title={config.title} blurb={config.blurb} count={count} />
      <div className="space-y-1">{children}</div>
    </section>
  );
};

const SkeletonSection = () => (
  <div className="space-y-3">
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-xl bg-gray-100 animate-pulse" />
      <div className="space-y-1.5">
        <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
        <div className="h-3 w-48 bg-gray-100 rounded animate-pulse" />
      </div>
    </div>
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-12 bg-gray-50 rounded-lg animate-pulse" />
      ))}
    </div>
  </div>
);

const NeedsAttention = forwardRef<HTMLDivElement, NeedsAttentionProps>(({ data, isLoading, isError }, ref) => {
  if (isLoading) {
    return (
      <div ref={ref} className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6 mb-6">
        <SkeletonSection />
      </div>
    );
  }

  const total = data
    ? data.escalations.length + data.lowSentiment.length + data.negativeFeedback.length + data.silentGenies.length
    : 0;

  if (!data || isError || total === 0) {
    return (
      <div ref={ref} className="bg-white rounded-2xl border border-gray-200 p-8 md:p-10 mb-6 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-genie-primary/10 mb-4">
          <GenieIcon name="smile" size={32} className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-bold text-[#222B45] mb-1">All clear this week</h2>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          No escalations, sentiment dips, negative feedback, or silent genies in the last 7 days. Nice work.
        </p>
      </div>
    );
  }

  return (
    <div ref={ref} className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6 mb-6">
      <div className="flex items-center mb-5">
        <GenieIcon name="eye" size={24} className="h-6 w-6 mr-2.5" />
        <div>
          <h2 className="text-lg font-bold text-[#222B45] leading-tight">Needs your attention</h2>
          <p className="text-xs text-[#64748B] mt-0.5">Last 7 days — sorted by recency</p>
        </div>
      </div>

      <div className="space-y-6">
        <SectionBlock bucket="escalations" count={data.escalations.length}>
          {data.escalations.map((item) => (
            <EscalationRow key={item.id} item={item} />
          ))}
        </SectionBlock>

        <SectionBlock bucket="lowSentiment" count={data.lowSentiment.length}>
          {data.lowSentiment.map((item) => (
            <LowSentimentRow key={item.id} item={item} />
          ))}
        </SectionBlock>

        <SectionBlock bucket="negativeFeedback" count={data.negativeFeedback.length}>
          {data.negativeFeedback.map((item) => (
            <NegativeFeedbackRow key={item.id} item={item} />
          ))}
        </SectionBlock>

        <SectionBlock bucket="silentGenies" count={data.silentGenies.length}>
          {data.silentGenies.map((item) => (
            <SilentGenieRow key={item.agentId} item={item} />
          ))}
        </SectionBlock>
      </div>
    </div>
  );
});
NeedsAttention.displayName = "NeedsAttention";

export default NeedsAttention;
